<template>
    <div class="p-4">
        <div>
            <p class="lead text-center alert alert-info">
                <small>Select a payout method</small>
            </p>

            <div class="row d-flex justify-content-center align-items-center">
                <div
                    class="col-lg-5 col-5 payout-card d-flex flex-column justify-content-center align-items-center text-center position-relative"
                    :class="{'selected': selectedPayoutType === 'wallet'}"
                    @click="selectedPayoutType = 'wallet'"
                >
                    <i :class="['fas', 'select-icon', selectedPayoutType === 'wallet' ? 'fa-check-circle' : 'fa-circle']"></i>
                    <div>
                        <i class="text-muted fas fa-wallet fa-2x mb-2"></i>
                        <h5>Payout to Wallet</h5>
                        <small class="d-none d-sm-block">Sends funds to your Wallet.</small>
                    </div>
                </div>

                <div class=" d-none d-sm-block align-items-center justify-content-center">
                    <h4 class="text-muted">-OR-</h4>
                </div>

                <div
                    class="col-lg-5 col-5 payout-card d-flex flex-column justify-content-center align-items-center text-center position-relative"
                    :class="{'selected': selectedPayoutType === 'bank'}"
                    @click="selectedPayoutType = 'bank'"
                >
                    <i :class="['fas', 'select-icon', selectedPayoutType === 'bank' ? 'fa-check-circle' : 'fa-circle']"></i>
                    <div>
                        <i class="text-muted fas fa-university fa-2x mb-2"></i>
                        <h5>Payout to Bank</h5>
                        <small class="d-none d-sm-block">Sends funds to your Bank.</small>
                    </div>
                </div>

            </div>

            <p v-if="selectedPayoutType === 'wallet' && wallet.available_balance === 0" class="lead text-center alert alert-danger mt-4">
                <span  class="">
                    <span class="text-3">There are currently no funds available for payout to your Wallet.</span><br>
                </span>

            </p>

            <p v-if="selectedPayoutType === 'bank' && wallet.net_payout === 0" class="lead text-center alert alert-danger mt-4">
                <span>
                    <span class="text-3">There are currently no funds available for payout to your Bank.</span><br>
                </span>

            </p>
            <p v-if="selectedPayoutType === 'bank' && wallet.net_payout !== 0" class="lead text-center alert alert-success mt-4">
                <span class="text-3">You are paying out funds to</span><br>
                <span class="font-weight-500"
                      v-for="i in wallet.corporate.banks.filter(bank => bank.currency === wallet.currency)"
                      :key="i.id">
                       <i class="fas fa-university"></i> {{ i.bank_name }} •••• {{ i.account_number.toString().slice(-4) }}
                    </span>
            </p>

            <p class="mt-5">
                Statement Balance
                <i class="fas fa-info-circle info-icon" data-toggle="tooltip" data-placement="top" title="The total balance in your wallet, reflecting all your transactions."></i>
                <span class="text-3 float-end"> {{ money(wallet.actual_balance) }}  {{ wallet.currency }}</span>
            </p>
            <p class="text-muted">
                Pending Balance
                <i class="fas fa-info-circle info-icon" data-toggle="tooltip" title="Funds in the holding period that will become available after clearance."></i>
                <span class="float-end d-flex align-items-center">{{ money(wallet.holding_funds) }} {{ wallet.currency }}</span>
            </p>
            <p v-if="selectedPayoutType === 'bank'">
                Available Balance
                <i class="fas fa-info-circle info-icon" data-toggle="tooltip" title="The amount you can request to withdraw immediately, excluding pending clearance."></i>
                <span class="float-end d-flex align-items-center">{{ money(wallet.available_balance) }}  {{ wallet.currency }}</span>
            </p>

            <p class="text-muted" v-if="selectedPayoutType === 'bank'">
                Payout fees
                <span class="float-end d-flex align-items-center">{{ money(wallet.payout_fee) }}  {{ wallet.currency }}</span>
            </p>

            <hr>

            <p class="text-4 font-weight-500">
                {{  selectedPayoutType === 'bank' ? 'Net Payout Value' : 'Available Balance'}}
                <i class="fas fa-info-circle info-icon" data-toggle="tooltip"
                   :title="selectedPayoutType === 'bank' ? 'The final amount you will receive after deducting payout fees.':
                   'The amount you can request to withdraw immediately, excluding pending clearance.'">
                </i>

                <span class="float-end">{{ selectedPayoutType === 'bank' ? money(wallet.net_payout) : money(wallet.available_balance)  }}  {{ wallet.currency }}</span>
            </p>

            <hr>

            <div v-if="selectedPayoutType === 'wallet' && wallet.available_balance !== 0 " class="mt-4">
                <label for="walletAmount">Enter Amount (Max: {{ money(wallet.available_balance) }} {{ wallet.currency }})</label>
                <input
                    type="number"
                    v-model="walletAmount"
                    class="form-control"
                    :max="wallet.net_payout"
                    :min="1"
                >
            </div>


            <!-- Submit Button -->
            <button
                @click="confirmPayout"
                :class="{
                        'btn-loading': loading,
                        'bg-dark-5 disabled': !selectedPayoutType ||
                          (selectedPayoutType === 'wallet' && (!walletAmount || walletAmount > wallet.available_balance)) ||
                          (selectedPayoutType === 'bank' && wallet.net_payout === 0)
                }"
                :disabled="!selectedPayoutType ||
               (selectedPayoutType === 'wallet' && (!walletAmount || walletAmount > wallet.available_balance)) ||
               (selectedPayoutType === 'bank' && wallet.net_payout === 0)"
                class="btn btn-success btn-block shadow-none mt-5">
                {{ loading ? "Processing..." : "Submit Payout Request" }}
            </button>


        </div>
    </div>
</template>

<script>
import { money } from "../../../../../core/forms/money";

export default {
    name: "PayoutForm",
    mixins: [money],
    props: ['wallet'],
    data() {
        return {
            loading: false,
            selectedPayoutType: null,
            walletAmount: null
        };
    },
    methods: {
        confirmPayout() {
            if (!this.selectedPayoutType) {
                window.alerts.error("Please select a payout option.");
                return;
            }

            let confirmationMessage = this.selectedPayoutType === 'wallet'
                ? `Are you sure you want to transfer ${this.money(this.walletAmount)} ${this.wallet.currency} to your Corporate Wallet?`
                : "Are you sure you want to proceed with the payout request to your bank?";

            window.alerts.confirm(confirmationMessage)
                .then((confirmed) => {
                    if (confirmed) {
                        this.loading = true;
                        this.submitPayout();
                    }
                });
        },
        submitPayout() {
            let payoutRoute = this.selectedPayoutType === 'wallet'
                ? `/business/wallets/${this.$route.params.id}/payout-wallet-to-wallet`
                : `/business/wallets/${this.$route.params.id}/payout-wallet-to-bank`;

            let payload = this.selectedPayoutType === 'wallet'
                ? { amount: this.walletAmount }
                : {};

            window.axios.post(payoutRoute, payload)
                .then(() => {
                    setTimeout(() => {
                        this.$router.push(`/business/wallets/payout/${this.$route.params.id}/confirmation`);
                    }, 3000);
                })
                .catch(() => {
                    this.message = "Payout Request Failed";
                });
        }

    },
};
</script>

<style scoped>
.float-end {
    float: right !important;
}

.payout-card {
    padding: 20px;
    border: 2px solid #ddd;
    border-radius: 8px;
    text-align: center;
    cursor: pointer;
    transition: all 0.3s ease-in-out;
    margin: 10px;
    position: relative;
}

.payout-card:hover {
    border-color: #252c65;
}

.selected {
    border-color: #28a745 !important;
    background-color: #e9f5e9;
}

.select-icon {
    position: absolute;
    top: 10px;
    right: 10px;
    font-size: 1.5rem;
    color: #ddd;
}

.selected .select-icon {
    color: #28a745;
}
</style>
